import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Gauge, ShieldCheck, Sparkles, Headphones, ExternalLink } from 'lucide-react';
import { useLang } from '../components/LanguageProvider';
import translations from '../translations';

/**
 * HAKKIMIZDA.
 *
 * Kaynak: crm-whowsell/src/app/about/about-content.tsx
 * Bölüm düzeni birebir korundu: rozet · gradyanlı başlık · giriş
 * paragrafı · misyon kutusu · dört değer kartı. Kaynaktaki
 * `Gauge / ShieldCheck / Sparkles / Headphones` ikon dizisi ve sırası
 * aynen alındı.
 *
 * SAPMALAR VE GEREKÇELERİ
 *  1. Kaynak Next.js/TypeScript ve `--crm-*` CSS değişkenleri üzerine
 *     kurulu. Burada CRA + saf JS var, o değişkenler yok: her renk bu
 *     projenin Tailwind `dark:` / açık tema desenine çevrildi.
 *  2. Kaynak `PublicFrame` + `DottedSurface` + `Reveal` sarmalayıcılarını
 *     kullanıyor; bu projede o bileşenler yok. Yerine sitenin kendi arka
 *     plan halesi kullanıldı. Bu projedeki `SectionReveal` bilinçli
 *     olarak KULLANILMADI: o bileşen içeriğin üstüne 200px'lik bir
 *     ayraç çizgisi çiziyor — ana sayfanın bölüm ayracı için doğru,
 *     ızgara içindeki kartların her birinin üstünde yanlış olurdu.
 *  3. Kaynak metin yalnızca CRM'i anlatıyor. Burası çatı site olduğu
 *     için giriş ve misyon dört ürüne genişletildi ve kaynakta
 *     bulunmayan "dört ürün" şeridi ile iletişim çağrısı eklendi —
 *     CRM'de bu iş üst şerit ve altbilgi tarafından yapılıyordu,
 *     burada sayfanın kendisi çatıyı anlatmak zorunda.
 */

const DEGER_IKONLARI = [Gauge, ShieldCheck, Sparkles, Headphones];

export default function AboutPage() {
  const { lang } = useLang();
  const t = translations[lang].about;
  const ortak = translations[lang].common;

  return (
    <div className="min-h-screen dark:bg-slate-950 bg-[#f0f4f8] dark:text-white text-slate-900 pt-20">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
        <div className="mb-8 sm:mb-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm dark:text-white/50 text-slate-500 hover:dark:text-white hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {ortak.home}
          </Link>
        </div>

        {/* BAŞLIK — kaynaktaki crm-badge / crm-h1-page / crm-lead üçlüsü.
            Vurgulu kısım gradyanlı, önündeki kısım düz: kaynakta
            `title_pre` + `<span class="crm-grad-text">title_accent</span>`
            neyse burada da o. */}
          <header className="max-w-3xl mb-12 sm:mb-16">
            <span className="inline-block mb-4 px-3 py-1 rounded-full text-[11px] font-semibold tracking-wide uppercase border dark:border-cyan-500/30 border-cyan-500/40 dark:bg-cyan-500/10 bg-cyan-50 dark:text-cyan-300 text-cyan-700">
              {t.badge}
            </span>
            <h1 className="text-[28px] leading-tight sm:text-4xl lg:text-5xl font-bold mb-5 break-words">
              <span className="dark:text-white text-slate-900">{t.titlePre}</span>
              <span className="animated-gradient-text">{t.titleAccent}</span>
            </h1>
            <p className="text-sm sm:text-base lg:text-lg leading-relaxed dark:text-white/60 text-slate-600 break-words">
              {t.intro}
            </p>
          </header>

        {/* MİSYON — kaynaktaki crm-mission bloğu. Kaynakta soldan renkli
            bir çizgiyle ayrılıyordu; burada aynı işlevi sol kenar çizgisi
            ve hafif gradyan zemin görüyor. */}
          <section className="glow-card rounded-2xl p-6 sm:p-8 lg:p-10 mb-12 sm:mb-16 border-l-2 dark:border-l-cyan-500/50 border-l-cyan-500/60"
            style={{ '--glow-color': 'rgba(6, 182, 212, 0.3)' }}>
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold mb-3 dark:text-white text-slate-900">
              {t.missionTitle}
            </h2>
            <p className="text-sm sm:text-base leading-relaxed dark:text-white/60 text-slate-600 break-words">
              {t.missionText}
            </p>
          </section>

        {/* DÖRT DEĞER — kaynaktaki crm-value-grid.
            320px'de tek sütun, 640px'den itibaren iki sütun. Dört kartı
            geniş ekranda dört sütuna açmak kaynakta da yoktu; kart
            metinleri cümle uzunluğunda olduğu için iki sütun daha okunur. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-12 sm:mb-16">
          {t.values.map((deger, i) => {
            const Ikon = DEGER_IKONLARI[i] || Sparkles;
            return (
              <div key={i} className="glow-card rounded-2xl p-5 sm:p-7 h-full" style={{ '--glow-color': 'rgba(6, 182, 212, 0.25)' }}>
                  <span className="inline-flex w-11 h-11 rounded-xl items-center justify-center mb-4 dark:bg-cyan-500/10 bg-cyan-50 border dark:border-cyan-500/20 border-cyan-500/25 dark:text-cyan-300 text-cyan-600">
                    <Ikon className="w-5 h-5" />
                  </span>
                  <h3 className="text-[15px] sm:text-base font-bold mb-2 dark:text-white text-slate-900">
                    {deger.title}
                  </h3>
                  <p className="text-[13px] sm:text-sm leading-relaxed dark:text-white/60 text-slate-600 break-words">
                    {deger.text}
                  </p>
                </div>
            );
          })}
        </div>

        {/* DÖRT ÜRÜN — çatı sitede kaynağa eklenen bölüm; gerekçesi
            dosya başındaki 3. sapma notunda. */}
          <h2 className="text-lg sm:text-xl lg:text-2xl font-bold mb-5 sm:mb-6 dark:text-white text-slate-900">
            {t.productsTitle}
          </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-12 sm:mb-16">
          {t.products.map((urun, i) => (
            <a
              key={i}
              href={urun.href}
              target="_blank"
                rel="noopener noreferrer"
                className="glow-card rounded-2xl p-5 sm:p-7 h-full block group"
                style={{ '--glow-color': 'rgba(139, 92, 246, 0.25)' }}
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <h3 className="text-[15px] sm:text-base font-bold dark:text-white text-slate-900 break-words">
                    {urun.title}
                  </h3>
                  <ExternalLink className="w-4 h-4 shrink-0 mt-0.5 dark:text-white/30 text-slate-400 group-hover:dark:text-cyan-400 group-hover:text-cyan-600 transition-colors" />
                </div>
                <p className="text-[13px] sm:text-sm leading-relaxed dark:text-white/60 text-slate-600 break-words">
                  {urun.text}
                </p>
              </a>
          ))}
        </div>

        {/* İLETİŞİM ÇAĞRISI */}
          <section className="glow-card rounded-2xl p-6 sm:p-8 lg:p-10 text-center" style={{ '--glow-color': 'rgba(6, 182, 212, 0.3)' }}>
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold mb-3 dark:text-white text-slate-900">
              {t.ctaTitle}
            </h2>
            <p className="text-[13px] sm:text-sm leading-relaxed dark:text-white/60 text-slate-600 max-w-xl mx-auto mb-6 break-words">
              {t.ctaText}
            </p>
            {/* Boyut `.wh-cta` sınıfından gelir (tek satıra kilitli,
                clamp ile ölçeklenir); burada yalnızca renk verilir. */}
            <Link
              to="/contact"
              className="wh-cta group bg-gradient-to-b from-cyan-400 to-cyan-500 hover:from-cyan-300 hover:to-cyan-400 text-slate-950 transition-all shadow-[0_0_0_1px_rgba(6,182,212,0.35),0_10px_30px_rgba(6,182,212,0.25)] active:scale-[0.97]"
            >
              {t.ctaButton}
              <ArrowRight className="group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </section>
      </div>
    </div>
  );
}
